/* ============
 * Client Groups Index
 * ============
 *
 * This page shows the list of client groups.
 */

import { mapGetters, mapMutations } from 'vuex';
import SearchMixin from './../../../../mixins/search';
import clientGroupService from './../../../../services/client-group';
import accountService from './../../../../services/account';

export default {
  data() {
    return {
      loading: false,
      sort: 'clients_count|desc',
      sortTypes: [
        {
          id: 'clients_count|desc',
          name: this.$t('sort.clientsCountDesc'),
        },
        {
          id: 'clients_count|asc',
          name: this.$t('sort.clientsCountAsc'),
        },
        {
          id: 'client_groups.name|asc',
          name: `${this.$t('sort.name')} (A -> Z)`,
        },
        {
          id: 'client_groups.name|desc',
          name: `${this.$t('sort.name')} (Z -> A)`,
        },
      ],
      limit: 25,
    };
  },

  computed: {
    ...mapGetters({
      clientGroups: 'paginatedClientGroups',
    }),
  },

  watch: {
    /**
     * Listem to router changes, used to check query params that have changed to apply filters.
     *
     * @param  {Object} route Current route object.
     */
    $route() {
      this.all(1, this.limit);
    },
  },

  components: {
    VCard: require('./../../../../components/card/card.vue'),
    VPagination: require('./../../../../components/paginationNew/paginationNew.vue'),
    VSelect: require('./../../../../components/form/select/select.vue'),
    VNoData: require('./../../../../components/noData/noData.vue'),
  },

  mounted() {
    this.setInnerSidebar([
      {
        title: this.$t('layouts.sidebar.clients'),
        options: [
          {
            title: this.$t('layouts.sidebar.active'),
            route: {
              name: 'clients.index',
              query: {
                filter: 'active',
              },
            },
          },
          {
            title: this.$t('layouts.sidebar.inActive'),
            route: {
              name: 'clients.index',
              query: {
                filter: 'inActive',
              },
            },
          },
          {
            title: this.$t('layouts.sidebar.orderedPastWeek'),
            route: {
              name: 'clients.index',
              query: {
                filter: 'orderedPastWeek',
              },
            },
          },
          {
            title: this.$t('layouts.sidebar.orderedPastMonth'),
            route: {
              name: 'clients.index',
              query: {
                filter: 'orderedPastMonth',
              },
            },
          },
          {
            title: this.$t('layouts.sidebar.neverOrdered'),
            route: {
              name: 'clients.index',
              query: {
                filter: 'neverOrdered',
              },
            },
          },
          {
            title: this.$t('layouts.sidebar.storeSynced'),
            route: {
              name: 'clients.index',
              query: {
                filter: 'storeSynced',
              },
            },
          },
          {
            title: this.$t('layouts.sidebar.all'),
            route: {
              name: 'clients.index',
            },
          },
        ],
        button: {
          title: this.$t('button.addClient'),
          route: {
            name: 'clients.create',
          },
        },
      },
      {
        title: this.$t('layouts.sidebar.clientGroup'),
        options: [
          {
            title: this.$t('layouts.sidebar.latest'),
            route: {
              name: 'clientGroups.index',
              query: {
                filter: 'latest',
              },
            },
          },
          {
            title: this.$t('layouts.sidebar.pastMonth'),
            route: {
              name: 'clientGroups.index',
              query: {
                filter: 'pastMonth',
              },
            },
          },
          {
            title: this.$t('layouts.sidebar.all'),
            route: {
              name: 'clientGroups.index',
            },
          },
        ],
        button: {
          title: this.$t('button.addClientGroup'),
          route: {
            name: 'clientGroups.create',
          },
        },
      },
      {
        title: this.$t('layouts.sidebar.clientAgreements'),
        options: [
          { title: this.$t('layouts.sidebar.all'), route: { name: 'pricingAgreements.index' } },
          { title: this.$t('layouts.sidebar.importAgreement'), route: { name: 'pricingAgreements.index', query: { import: '1' } } },
        ],
        button: {
          title: this.$t('layouts.sidebar.newAgreement'),
          route: { name: 'pricingAgreements.create' },
        },
      },
    ]);

    accountService.rememberPerPage('get', { from: 'client_group' })
      .then((data) => {
        this.limit = data.size;
        this.all(1, this.limit);
      }).catch((error) => {
        console.log(error);
      });
  },

  destroyed() {
    this.unsetInnerSidebar();
  },

  methods: {
    ...mapMutations({
      setInnerSidebar: 'SET_INNERSIDEBAR',
      unsetInnerSidebar: 'UNSET_INNERSIDEBAR',
    }),

    perPage(size) {
      accountService.rememberPerPage('post', { from: 'client_group', perPage: size })
        .then((data) => {
          this.limit = data.size;
        }).catch((error) => {
          console.log(error);
        });
    },

    /**
     * Fetches paginated client groups.
     *
     * @param  {int} page The page number.
     * @param  {int} limit The number of items displayed on a page.
     */
    all(page, limit) {
      this.loading = true;
      clientGroupService.items({
        page,
        limit,
        sort: this.sort,
        search: this.getSearch(this.limit),
        filter: this.$route.query.filter,
      }).then(() => {
        this.loading = false;
      }).catch(() => {
        this.loading = false;
      });
    },

    /**
     * Delete specific client group.
     *
     * @param  {Object} clientGroup The client group object.
     */
    deleteClientGroup(clientGroup) {
      this.$confirm(this.$t('static.clientGroups.index.confirmDelete'), this.$t('static.clientGroups.index.confirmDeleteTitle'), {
        confirmButtonText: this.$t('button.delete'),
        cancelButtonText: this.$t('button.cancel'),
        type: 'warning',
      }).then(() => {
        clientGroupService.destroy(clientGroup.id)
          .catch(({ response }) => {
            const message = response.data.error.code === 40114 ? this.$t('static.clientGroups.index.deleteHasClientsError') :
              this.$t('static.clientGroups.index.deleteError');
            this.$message({
              showClose: true,
              message,
              type: 'error',
            });
          });
      }).catch(() => {

      });
    },

    /**
     * Go to edit view for client group.
     *
     * @param  {Object} clientGroup The client group object.
     */
    editClientGroup(clientGroup) {
      this.$router.push({
        name: 'clientGroups.edit',
        params: { id: clientGroup.id },
      });
    },

    goToClients(id, e) {
      e.stopPropagation();
      this.$router.push({ name: 'clients.index', query: { filter: `clientGroup,${id}` } });
    },
  },

  mixins: [
    SearchMixin,
  ],

};



// WEBPACK FOOTER //
// ./src/app/pages/withNavigation/clientGroups/index/index.js